import { EyeInvisibleOutlined, EyeTwoTone, FileUnknownOutlined, LinkOutlined } from '@ant-design/icons';
import { AutoComplete, Button, Checkbox, ColorPicker, DatePicker, Input, InputNumber, Select, Tag } from 'antd';
import enLocale from 'antd/es/date-picker/locale/en_GB';
import ruLocale from 'antd/es/date-picker/locale/ru_RU';
import ukLocale from 'antd/es/date-picker/locale/uk_UA';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import React, { memo } from 'react';
import { type Control, Controller, type FieldValues, useFieldArray, useWatch } from 'react-hook-form';
import { IoCloudOffline } from 'react-icons/io5';
import PasswordStrengthBar from 'react-password-strength-bar';

import CaptchaField from '~/shared/components/form-fields/captcha-field/captcha-field';
import DropZoneField from '~/shared/components/form-fields/drop-zone-field';
import FilesUploaderField from '~/shared/components/form-fields/files-uploader-field';
import FormGroup from '~/shared/components/form-fields/form-group/form-group';
import HTMLAreaField from '~/shared/components/form-fields/html-area-field/html-area-field';
import ImageField from '~/shared/components/form-fields/image-field/image-field';
import ItemsListField from '~/shared/components/form-fields/items-list-field/items-list-field';
import RadioGroupField from '~/shared/components/form-fields/radio-group-field/radio-group-field';
import TermsAndConditions from '~/shared/components/terms-and-conditions/terms-and-conditions';
import { LANGUAGE } from '~/shared/constants';
import type { FormFieldType } from '~/shared/types/form-type';

const PhoneInput = dynamic(() => import('antd-phone-input'), { ssr: false });
const ImageCropModal = dynamic(() => import('~/shared/components/image-crop-modal/image-crop-modal'), {
  ssr: false,
});

const { RangePicker } = DatePicker;

const getEventValue = (evtOrValue: unknown) => {
  if (evtOrValue && typeof evtOrValue === 'object' && 'target' in evtOrValue) {
    return (evtOrValue as { target?: { value?: unknown } }).target?.value;
  }

  return evtOrValue;
};

const getDatePickerLocale = () => {
  if (typeof window === 'undefined') {
    return enLocale;
  }

  const [, lng] = window.location.pathname.split('/');

  if (lng === LANGUAGE.UA) {
    return ukLocale;
  }

  if (lng === LANGUAGE.RU) {
    return ruLocale;
  }

  return enLocale;
};

const getCheckboxValue = (evtOrValue: unknown) => {
  if (typeof evtOrValue === 'boolean') {
    return evtOrValue;
  }

  if (evtOrValue && typeof evtOrValue === 'object' && 'target' in evtOrValue) {
    const checked = (evtOrValue as { target?: { checked?: unknown } }).target?.checked;

    if (typeof checked === 'boolean') {
      return checked;
    }
  }

  return Boolean(evtOrValue);
};

type ExtendedRendererProps = {
  formField: FormFieldType;
  name: string;
  value: unknown;
  errors: Record<string, unknown>;
  fieldError?: string;
  watch: (fieldName?: string) => unknown;
  setValue: (fieldName: string, value: unknown, options?: Record<string, unknown>) => void;
  getValues: (fieldName?: string) => unknown;
  control: Control<FieldValues>;
  onChange?: (value: unknown) => void;
  append?: unknown;
};

type RendererResult = {
  ComponentByType: React.ComponentType<any>;
  afterField: React.ReactNode;
  props: Record<string, unknown>;
};

const buildSelectorRenderer = ({
  formField,
  value,
  onChange,
  watch,
  setValue,
  getValues,
  name,
}: ExtendedRendererProps): RendererResult => {
  const basePath = name.split('.').slice(0, -1).join('.');

  return {
    ComponentByType: Select,
    afterField: null,
    props: {
      value: formField?.config?.getValue ? formField.config.getValue(value) : value,
      size: formField?.config?.size ?? 'large',
      disabled: formField?.config?.checkIsDisabled
        ? formField.config.checkIsDisabled(getValues as never, name as never, watch as never)
        : formField?.config?.disabled,
      options: formField?.config?.getOptions
        ? formField.config.getOptions(watch, getValues, name, { basePath })
        : formField?.config?.options,
      mode: formField?.config?.mode,
      allowClear: formField?.config?.allowClear ?? formField?.config?.isClearable,
      showSearch: formField?.config?.showSearch,
      maxTagCount: formField?.config?.maxTagCount,
      placeholder: formField?.config?.placeholder,
      optionFilterProp: formField?.config?.optionFilterProp,
      filterOption: formField?.config?.filterOption,
      onChange: formField?.config?.onChange
        ? (nextValue: unknown, data: unknown) =>
            formField.config.onChange({
              value: nextValue,
              name,
              data,
              setValue,
              getValues,
            })
        : onChange,
      loading: formField?.config?.isLoading,
    },
  };
};

const buildDateRenderer = ({
  formField,
  value,
  onChange,
  setValue,
  getValues,
  watch,
  name,
}: ExtendedRendererProps): RendererResult => {
  const ComponentByType = formField.type === 'range-picker' ? RangePicker : DatePicker;

  return {
    ComponentByType,
    afterField: null,
    props: {
      locale: formField?.config?.locale ?? getDatePickerLocale(),
      value: formField?.config?.getValue ? formField.config.getValue(value) : value,
      size: 'large',
      disabled: formField?.config?.checkIsDisabled
        ? formField.config.checkIsDisabled(getValues as never, name as never, watch as never)
        : formField?.config?.disabled,
      showTime: formField?.config?.showTime,
      allowClear: formField?.config?.allowClear,
      format: formField?.config?.format,
      onCalendarChange: (nextValue: unknown) => {
        setValue(name, nextValue, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      },
      needConfirm: formField?.config?.needConfirm,
      changeOnBlur: formField?.config?.changeOnBlur,
      style: formField?.config?.style,
      className: formField?.config?.className,
      separator: formField?.config?.separator,
      onChange: formField?.config?.onChange
        ? (nextValue: unknown) =>
            formField.config.onChange({
              value: nextValue,
              name,
              setValue,
              getValues,
            })
        : onChange,
    },
  };
};

const buildAutoCompleteRenderer = ({
  formField,
  value,
  onChange,
  setValue,
  getValues,
  watch,
  name,
}: ExtendedRendererProps): RendererResult => {
  return {
    ComponentByType: AutoComplete,
    afterField: null,
    props: {
      value: formField?.config?.getValue ? formField.config.getValue({ value, setValue }) : value,
      disabled: formField?.config?.disabled,
      size: 'large',
      optionRender: formField?.config?.optionRender ?? null,
      options: formField?.config?.getOptions
        ? formField.config.getOptions(watch, getValues)
        : formField?.config?.options,
      onChange: formField?.config?.onChange
        ? (nextValue: unknown, data: unknown) =>
            formField.config.onChange({
              value: nextValue,
              data,
              name,
              setValue,
              getValues,
            })
        : onChange,
      ...(formField?.config?.onSelect && {
        onSelect: (nextValue: unknown, data: unknown) =>
          formField.config.onSelect({
            value: nextValue,
            setValue,
            name,
            data,
          }),
      }),
      ...(formField?.config?.onSearch && {
        onSearch: (nextValue: unknown, data: unknown) =>
          formField.config.onSearch({
            value: nextValue,
            setValue,
            name,
            data,
          }),
      }),
      ...(formField?.config?.placeholder && {
        placeholder: formField.config.placeholder,
      }),
      loading: formField?.config?.isLoading,
    },
  };
};

const buildTagsSelectorRenderer = ({
  formField,
  value,
  onChange,
  setValue,
  getValues,
  name,
}: ExtendedRendererProps): RendererResult => {
  return {
    ComponentByType: Select,
    afterField: null,
    props: {
      value: ((value as { name?: string }[] | undefined) ?? []).map((option) => option?.name ?? option),
      size: 'large',
      disabled: formField?.config?.disabled,
      loading: formField?.config?.isLoading,
      mode: formField?.config?.mode,
      allowClear: formField?.config?.isClearable,
      options: formField?.config?.options
        ? formField.config.options
        : ((value as { name?: string }[] | undefined) ?? []).map((option) => ({
            label: option?.name ?? option,
            value: option?.name ?? option,
          })),
      onChange: (nextValue: unknown, optionData: unknown) => {
        if (formField?.config?.onChange) {
          formField.config.onChange({
            setValue,
            name,
            value: Array.isArray(optionData) ? optionData : [],
            getValues,
          });
          return;
        }

        onChange?.(nextValue);
      },
      labelInValue: false,
    },
  };
};

const buildCaptchaRenderer = ({ formField, value, onChange, fieldError }: ExtendedRendererProps): RendererResult => {
  return {
    ComponentByType: CaptchaField,
    afterField: null,
    props: {
      name: formField.name,
      value: String(value ?? ''),
      onChange,
      error: fieldError,
      config: formField.config,
    },
  };
};

const buildTermsRenderer = ({ formField, value, fieldError, setValue }: ExtendedRendererProps): RendererResult => {
  return {
    ComponentByType: TermsAndConditions,
    afterField: null,
    props: {
      value: Boolean(value),
      onChange: (checked: boolean) => {
        setValue(formField.name, Boolean(checked), {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
      },
      error: fieldError,
      config: formField.config,
      text: formField?.config?.text,
    },
  };
};

const buildRadioRenderer = ({
  formField,
  onChange,
  errors,
  setValue,
  getValues,
  name,
}: ExtendedRendererProps): RendererResult => {
  return {
    ComponentByType: RadioGroupField,
    afterField: null,
    props: {
      config: formField?.config,
      errors,
      onChange: formField?.config?.onChange
        ? (nextValue: unknown, data: unknown) =>
            formField.config.onChange({
              value: nextValue,
              data,
              name,
              setValue,
              getValues,
            })
        : onChange,
    },
  };
};

const buildHtmlAreaRenderer = ({
  formField,
  value,
  onChange,
  setValue,
  getValues,
  name,
}: ExtendedRendererProps): RendererResult => {
  return {
    ComponentByType: HTMLAreaField,
    afterField: null,
    props: {
      value,
      name,
      setValue,
      onChange,
      config: formField?.config?.getConfig ? formField.config.getConfig() : formField?.config,
      getValues,
    },
  };
};

const buildCheckboxRenderer = ({
  formField,
  value,
  onChange,
  setValue,
  getValues,
  watch,
  name,
}: ExtendedRendererProps): RendererResult => {
  return {
    ComponentByType: Checkbox,
    afterField: null,
    props: {
      checked: Boolean(value),
      children: formField?.label,
      disabled: formField?.config?.checkIsDisabled
        ? formField.config.checkIsDisabled(getValues as never, name as never, watch as never)
        : formField?.config?.disabled,
      onChange: (evtOrValue: unknown) => {
        if (formField?.config?.onChange) {
          formField.config.onChange({
            value: evtOrValue,
            name,
            setValue,
            getValues,
          });
          return;
        }

        const checked = getCheckboxValue(evtOrValue);
        onChange?.(checked);
      },
      config: formField?.config,
    },
  };
};

const buildDropZoneRenderer = ({
  formField,
  value,
  setValue,
  onChange,
  name,
}: ExtendedRendererProps): RendererResult => {
  return {
    ComponentByType: DropZoneField,
    afterField: null,
    props: {
      value,
      setValue,
      name,
      onChange,
      config: formField?.config,
    },
  };
};

const buildImageRenderer = ({
  formField,
  value,
  setValue,
  getValues,
  onChange,
  name,
}: ExtendedRendererProps): RendererResult => {
  return {
    ComponentByType: ImageField,
    afterField: null,
    props: {
      value,
      setValue,
      getValues,
      name,
      onChange,
      changeStateOfConfirmRemovingModal: formField?.changeStateOfConfirmRemovingModal,
    },
  };
};

const buildItemsListRenderer = ({
  formField,
  value,
  watch,
  errors,
  append,
  setValue,
  control,
  getValues,
  onChange,
  name,
}: ExtendedRendererProps): RendererResult => {
  return {
    ComponentByType: ItemsListField,
    afterField: null,
    props: {
      value,
      watch,
      name,
      errors,
      append,
      setValue,
      control,
      getValues,
      onChange,
      config: formField?.config,
    },
  };
};

const buildFilesUploaderRenderer = ({ onChange, formField, setValue }: ExtendedRendererProps): RendererResult => {
  return {
    ComponentByType: FilesUploaderField,
    afterField: null,
    props: {
      setValue,
      onChange,
      config: formField?.config,
    },
  };
};

const buildFilesManagerRenderer = ({ formField, setValue, getValues }: ExtendedRendererProps): RendererResult => {
  if (formField?.config?.files?.length > 0) {
    const ComponentByType = () => (
      <>
        <Button
          className="action-button--yellow-bordered"
          size="large"
          onClick={() => formField?.config?.onClick?.({ getValues })}
        >
          {formField?.config?.title}
        </Button>

        <div className="files-manager__fies-block">
          {formField?.config?.files?.map((file: any, index: number) => {
            const handleSlideChange = (nextValue: number | null) => {
              setValue(`${formField?.name}.${index}.slide`, nextValue);
            };

            let icon = <FileUnknownOutlined className="files-manager__file-icon" />;

            if (file?.data?.isNotHandout) {
              icon = (
                <div className="files-manager__public-not-access-to-file">
                  <IoCloudOffline />
                </div>
              );
            }

            if (file?.data?.isLink) {
              icon = (
                <div className="files-manager__file-icon">
                  <LinkOutlined />
                </div>
              );
            }

            return (
              <div className="files-manager__slide-block" key={file?._id}>
                <Tag
                  closeIcon
                  className="files-manager__tag"
                  onClose={(event) => {
                    event.preventDefault();
                    formField?.config?.onCloseButtonClick?.({
                      value: file?._id,
                      setValue,
                      getValues,
                    });
                  }}
                >
                  {icon}
                  <span className="files-manager__slide-name">{file?.name}</span>
                </Tag>

                {false && (
                  <div className="files-manager__slide-input">
                    <span>Slide</span>
                    <InputNumber
                      min={1}
                      defaultValue={getValues(`${formField?.name}.${index}.slide`) as number}
                      onChange={handleSlideChange}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </>
    );

    return {
      ComponentByType,
      afterField: null,
      props: {},
    };
  }

  return {
    ComponentByType: Button,
    afterField: null,
    props: {
      className: 'action-button--yellow-bordered',
      size: 'large',
      children: formField?.config?.title,
      onClick: formField?.config?.onClick?.bind(null, { getValues }),
    },
  };
};

const buildImageCropModalRenderer = ({
  formField,
  onChange,
  control,
  errors,
  watch,
  setValue,
  getValues,
  name,
}: ExtendedRendererProps): RendererResult => {
  const basePath = name.split('.').slice(0, -1).join('.');

  return {
    ComponentByType: ImageCropModal,
    afterField: null,
    props: {
      ...formField?.config,
      field: formField?.config?.field ?? basePath,
      onChange,
      control,
      errors,
      watch,
      setValue,
      getValues,
    },
  };
};

const buildPhoneRenderer = ({ formField, value, onChange }: ExtendedRendererProps): RendererResult => {
  return {
    ComponentByType: PhoneInput,
    afterField: null,
    props: {
      value,
      defaultValue: value,
      placeholder: formField?.placeholder,
      size: formField?.size,
      ref: formField?.ref,
      country: formField?.config?.country ?? 'ua',
      disabled: formField?.disabled,
      onChange,
      enableSearch: formField?.config?.enableSearch ?? false,
    },
  };
};
const buildColorRenderer = ({ formField, value, onChange }: ExtendedRendererProps): RendererResult => {
  return {
    ComponentByType: ColorPicker,
    afterField: null,
    props: {
      value,
      allowClear: formField?.config?.allowClear,
      onChange,
    },
  };
};

const buildInputNumberRenderer = ({
  formField,
  value,
  onChange,
  setValue,
  getValues,
  watch,
  name,
}: ExtendedRendererProps): RendererResult => {
  return {
    ComponentByType: InputNumber,
    afterField: null,
    props: {
      value,
      min: formField?.config?.min,
      max: formField?.config?.max,
      parser: formField?.config?.parser,
      formatter: formField?.config?.formatter,
      style: formField?.config?.style,
      className: formField?.config?.className,
      size: formField?.config?.size ?? 'large',
      disabled: formField?.config?.checkIsDisabled
        ? formField.config.checkIsDisabled(getValues as never, name as never, watch as never)
        : formField?.config?.disabled,
      onChange: formField?.config?.onChange
        ? (nextValue: unknown) =>
            formField.config.onChange({
              value: nextValue,
              name,
              setValue,
              getValues,
            })
        : onChange,
    },
  };
};

const buildPasswordRenderer = ({ formField, value, onChange }: ExtendedRendererProps): RendererResult => {
  return {
    ComponentByType: Input.Password,
    afterField: formField?.config?.withChecker ? <PasswordStrengthBar password={String(value ?? '')} /> : null,
    props: {
      value,
      size: 'large',
      placeholder: formField?.placeholder,
      ref: formField?.ref,
      iconRender: (visible: boolean) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />),
      disabled: formField?.disabled,
      onChange,
    },
  };
};

type CustomFieldRenderHostProps = Record<string, unknown> & {
  rendered?: unknown;
};

/**
 * Keep the component type stable between form updates.
 *
 * Previously buildCustomFieldRenderer created ComponentByType inline. Every
 * react-hook-form update therefore looked like a brand-new component type and
 * remounted stateful custom fields. A custom field containing useWatch/useEffect
 * could then mount -> setValue -> remount forever.
 */
const CustomFieldRenderHost = ({ rendered, ...componentProps }: CustomFieldRenderHostProps) => {
  if (!rendered) {
    return null;
  }

  if (React.isValidElement(rendered)) {
    return rendered;
  }

  if (typeof rendered === 'function') {
    const RenderComponent = rendered as React.ComponentType<any>;
    return <RenderComponent {...componentProps} />;
  }

  if (typeof rendered === 'object' && rendered !== null && '$$typeof' in rendered) {
    const RenderComponent = rendered as React.ComponentType<any>;
    return <RenderComponent {...componentProps} />;
  }

  return <>{rendered as React.ReactNode}</>;
};

const buildCustomFieldRenderer = ({
  formField,
  value,
  setValue,
  getValues,
  onChange,
  fieldError,
  watch,
}: ExtendedRendererProps): RendererResult => {
  const rendered = formField?.render?.({
    value,
    error: fieldError,
    setValue,
    getValues,
    watch,
  });

  return {
    ComponentByType: CustomFieldRenderHost,
    afterField: null,
    props: {
      rendered,
      value,
      placeholder: formField?.placeholder,
      ref: formField?.ref,
      disabled: formField?.disabled,
      onChange,
      ...formField?.config,
      ...(formField?.config?.onBlur && {
        onBlur: () => {
          formField.config.onBlur({
            value,
            setValue,
            getValues,
          });
        },
      }),
    },
  };
};

const buildGroupRenderer = ({
  formField,
  setValue,
  getValues,
  watch,
  control,
  errors,
}: ExtendedRendererProps): RendererResult => {
  return {
    ComponentByType: FormGroup,
    afterField: null,
    props: {
      formField,
      setValue,
      getValues,
      watch,
      control,
      errors,
    },
  };
};

const buildTextRenderer = ({
  formField,
  value,
  onChange,
  setValue,
  getValues,
}: ExtendedRendererProps): RendererResult => {
  const ComponentByType = formField?.type === 'text-area' ? Input.TextArea : Input;

  return {
    ComponentByType,
    afterField: null,
    props: {
      value,
      placeholder: formField?.placeholder,
      ref: formField?.ref,
      size: 'large',
      disabled: formField?.disabled,
      ...formField?.config,
      onChange: (evtOrValue: unknown, data?: unknown) => {
        const nextValue = getEventValue(evtOrValue);

        if (formField?.config?.onChange) {
          formField.config.onChange({
            value: nextValue,
            data,
            setValue,
            getValues,
          });
        }

        if (formField?.config?.onDebouncedChange) {
          window.clearTimeout(formField.config.__debounceTimer);

          formField.config.__debounceTimer = window.setTimeout(() => {
            formField.config.onDebouncedChange({
              value: nextValue,
              data,
              setValue,
              getValues,
            });
          }, formField.config.debounceMs ?? 600);
        }

        onChange?.(nextValue);
      },
      ...(formField?.config?.onBlur && {
        onBlur: () => {
          formField.config.onBlur({
            value,
            setValue,
            getValues,
          });
        },
      }),
    },
  };
};

const FIELD_RENDERERS: Partial<Record<FormFieldType['type'], (props: ExtendedRendererProps) => RendererResult>> = {
  selector: buildSelectorRenderer,
  'date-picker': buildDateRenderer,
  'range-picker': buildDateRenderer,
  'auto-complete': buildAutoCompleteRenderer,
  'tags-selector': buildTagsSelectorRenderer,
  captcha: buildCaptchaRenderer,
  group: buildGroupRenderer,
  'terms-and-conditions': buildTermsRenderer,
  'radio-selector': buildRadioRenderer,
  'html-area': buildHtmlAreaRenderer,
  checkbox: buildCheckboxRenderer,
  'drop-zone': buildDropZoneRenderer,
  image: buildImageRenderer,
  'items-list': buildItemsListRenderer,
  'files-uploader': buildFilesUploaderRenderer,
  'files-manager': buildFilesManagerRenderer,
  'image-crop-modal': buildImageCropModalRenderer,
  'phone-input': buildPhoneRenderer,
  'color-picker': buildColorRenderer,
  password: buildPasswordRenderer,
  'custom-field': buildCustomFieldRenderer,
  text: buildTextRenderer,
  'input-number': buildInputNumberRenderer,
  'text-area': buildTextRenderer,
};

const getFieldRenderer = (props: ExtendedRendererProps): RendererResult => {
  const renderer = FIELD_RENDERERS[props.formField.type] ?? buildTextRenderer;
  return renderer(props);
};

const Component = memo(
  ({
    name,
    control,
    watch,
    formField,
    setValue,
    getValues,
    errors,
    className,
    isArrayItem = false,
  }: {
    name: string;
    control: Control<FieldValues>;
    watch: (fieldName?: string) => unknown;
    formField: FormFieldType;
    setValue: (fieldName: string, value: unknown, options?: Record<string, unknown>) => void;
    getValues: (fieldName?: string) => unknown;
    errors: Record<string, unknown>;
    className?: string;
    isArrayItem?: boolean;
  }) => {
    useWatch({
      control,
      name: formField?.config?.deps ?? [],
    });

    return (
      <Controller
        name={name}
        control={control}
        rules={
          isArrayItem
            ? undefined
            : {
                validate: formField?.config?.getValidate
                  ? formField.config.getValidate(getValues, name)
                  : formField?.validate,
                ...formField?.config?.rules,
              }
        }
        render={({ field: { onChange, value, name: fieldName }, fieldState }) => {
          const fieldError = fieldState.error?.message;
          const isHidden = formField?.config?.checkIsHidden
            ? formField.config.checkIsHidden(getValues as never, fieldName as never, watch as never)
            : false;

          if (isHidden) {
            return null;
          }

          const { ComponentByType, afterField, props } = getFieldRenderer({
            formField,
            onChange,
            errors,
            fieldError,
            name: fieldName,
            watch,
            value,
            setValue,
            control,
            getValues,
          });

          return (
            <div className={classNames('form-block__field-block', { 'has-error': Boolean(fieldError) }, className)}>
              {formField?.type !== 'checkbox' && formField?.type !== 'group' && formField?.label ? (
                <div className="form-block__label">
                  {formField.label}
                  {formField.required && <div className="form-block__required">*</div>}
                </div>
              ) : null}

              <div className="form-block__field-container">
                <ComponentByType
                  name={fieldName}
                  type={formField?.type}
                  value={value}
                  {...props}
                  suppressHydrationWarning
                />

                {afterField}

                {fieldError ? (
                  <div className="ant-form-item-explain ant-form-item-explain-error">{fieldError}</div>
                ) : null}
              </div>
            </div>
          );
        }}
      />
    );
  },
);

type FormErrorValue = {
  message?: string;
  root?: {
    message?: string;
  };
};

const getNestedValue = (source: unknown, path: string): unknown => {
  if (!source || typeof source !== 'object') {
    return undefined;
  }

  return path.split('.').reduce<unknown>((acc, key) => {
    if (!acc || typeof acc !== 'object') {
      return undefined;
    }

    return (acc as Record<string, unknown>)[key];
  }, source);
};

const getArrayFieldError = (errors: Record<string, unknown>, name: string): string | null => {
  const error = getNestedValue(errors, name) as FormErrorValue | undefined;

  return error?.message ?? error?.root?.message ?? null;
};

export const Field = ({
  formField,
  fieldConfig,
  setValue,
  watch,
  getValues,
  className,
  control,
  errors,
}: {
  formField: FormFieldType;
  fieldConfig?: { name?: string };
  className?: string;
  errors: Record<string, unknown>;
  getValues: (fieldName?: string) => unknown;
  setValue: (fieldName: string, value: unknown, options?: Record<string, unknown>) => void;
  watch: (fieldName?: string) => unknown;
  control: Control<FieldValues>;
}) => {
  const name = fieldConfig?.name ? `${fieldConfig.name}.${formField.name}` : formField.name;

  if (formField?.config?.isArray) {
    const { fields, append, remove } = useFieldArray({
      control,
      name,
      keyName: '__rhfId',
    });

    return (
      <>
        {fields.map((item: Record<string, unknown>, index: number) => (
          <div key={String(item.__rhfId)}>
            <Component
              watch={watch}
              name={`${name}.${index}`}
              control={control}
              formField={formField}
              setValue={setValue}
              getValues={getValues}
              errors={errors}
              className={className}
              isArrayItem
            />

            {formField?.config?.removeItemButton?.render?.({
              remove: (removeIndex: number) => {
                remove(removeIndex);

                const currentItems = (getValues(name) ?? []) as unknown[];

                setValue(name, currentItems, {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                });
              },
              index,
              setValue,
              getValues,
            })}
          </div>
        ))}

        {formField?.config?.addItemButton?.render?.({
          append: (value: unknown) => {
            append(value);

            const currentItems = (getValues(name) ?? []) as unknown[];

            setValue(name, currentItems, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });
          },
          setValue,
          getValues,
        })}

        <Controller
          name={name}
          control={control}
          rules={{
            validate: formField?.validate,
          }}
          render={({ fieldState }) => {
            const arrayFieldError = fieldState.error?.message ?? getArrayFieldError(errors, name);

            return arrayFieldError ? (
              <div className="ant-form-item-explain ant-form-item-explain-error">{arrayFieldError}</div>
            ) : null;
          }}
        />
      </>
    );
  }

  return (
    <Component
      name={name}
      watch={watch}
      control={control}
      formField={formField}
      setValue={setValue}
      getValues={getValues}
      errors={errors}
      className={className}
    />
  );
};

export default Field;
